/**
 * The agent's face: its avatar image (generated or uploaded), or « Perso », its living drawn character
 * (docs/design/APP-APPLE.md §4; geometry and colors shared with the Worker's contact card: src/shared/character.ts).
 *
 * The character breathes and floats, blinks at random (1.8 to 5.6 s, sometimes twice), follows the mouse or the finger
 * anywhere on the page with its eyes (and leans toward it, wider eyes and a little smile when it comes close), looks
 * around by itself after 2.5 s without a move, hops when clicked (« boop »), and wears the agent's real mood: repos,
 * ecoute (the owner writes), reflechit (a turn runs), parle (the reply streams), joie (done, approved). One shared
 * pointer listener feeds every character on the page (one animation frame per move); the eyes are updated in place,
 * never through a React render. prefers-reduced-motion stops the floating, breathing, rings and jumps: the gaze and the
 * blinks stay, without transition (styles.css, .pz).
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { AgentAvatar, AgentCharacter } from "../../shared/contracts";
import {
  CHARACTER_COLOR_HEX,
  CHARACTER_GEOMETRY,
  characterViewBox,
  EYE,
  happyEyesPath,
  inkOf,
  lookOf,
  SMILE_PATH,
  type CharacterLook,
  type CharacterMood,
} from "../../shared/character";
import { fileUrl } from "../lib/api";
import type { PresenceState } from "../lib/presence";
import { IconComputer, IconPause } from "./icons";
import { cx } from "./ui";

export {
  CHARACTER_COLOR_HEX,
  CHARACTER_COLOR_LABEL,
  CHARACTER_COLOR_LIST,
  CHARACTER_GEOMETRY,
  CHARACTER_SHAPE_LABEL,
  CHARACTER_SHAPE_LIST,
  characterLook,
  lookOf,
  normalizeLook,
  type CharacterColor,
  type CharacterLook,
  type CharacterMood,
  type CharacterShape,
} from "../../shared/character";

export interface AvatarAgent {
  name: string;
  avatar?: AgentAvatar | null;
  /** The look chosen in « Son visage » (AgentSettings.character); absent or null = the default face. */
  character?: AgentCharacter | null;
  /** false for an extra agent (« Mes agents »): its default face comes from its name. Default: the main agent (white « rond »). */
  main?: boolean;
}

/** The mood a presence state reads as. */
export function moodOf(status: PresenceState | undefined): CharacterMood {
  if (status === "thinking" || status === "working") return "reflechit";
  if (status === "computer") return "parle";
  return "repos";
}

// ───────────── One pointer for every character ─────────────

const pointer = { x: 0, y: 0, at: 0, seen: false };
const followers = new Set<() => void>();
let frame = 0;

function onPointer(e: PointerEvent): void {
  pointer.x = e.clientX;
  pointer.y = e.clientY;
  pointer.at = performance.now();
  pointer.seen = true;
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    for (const fn of [...followers]) fn();
  });
}

function follow(fn: () => void): () => void {
  if (!followers.size) {
    window.addEventListener("pointermove", onPointer, { passive: true });
    window.addEventListener("pointerdown", onPointer, { passive: true });
  }
  followers.add(fn);
  return () => {
    followers.delete(fn);
    if (!followers.size) {
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("pointerdown", onPointer);
    }
  };
}

const rand = (min: number, max: number) => min + Math.random() * (max - min);

interface Gaze {
  lx: number;
  ly: number;
  near: number;
}

/**
 * « Perso »: the drawn character, `size` px wide (1.1 × `size` tall with its ground shadow; a square crop when
 * `framed`). `animated={false}` draws it still (no motion, no gaze, no blink).
 */
export function Perso({
  look,
  mood = "repos",
  size,
  framed = false,
  animated = true,
  asleep = false,
  label,
  boop = true,
  className,
  style,
}: {
  look: CharacterLook;
  mood?: CharacterMood;
  size: number;
  framed?: boolean;
  animated?: boolean;
  asleep?: boolean;
  label?: string;
  boop?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const geo = CHARACTER_GEOMETRY[look.shape];
  const body = CHARACTER_COLOR_HEX[look.color];
  const ink = inkOf(look.color);
  const [eyeL, eyeR] = geo.eyes;
  const live = animated && !asleep;
  const happy = mood === "joie";

  const svgRef = useRef<SVGSVGElement>(null);
  const leanRef = useRef<SVGGElement>(null);
  const lookRef = useRef<SVGGElement>(null);
  const whiteL = useRef<SVGEllipseElement>(null);
  const whiteR = useRef<SVGEllipseElement>(null);
  const pupilL = useRef<SVGCircleElement>(null);
  const pupilR = useRef<SVGCircleElement>(null);
  const shineL = useRef<SVGCircleElement>(null);
  const shineR = useRef<SVGCircleElement>(null);
  const smileRef = useRef<SVGPathElement>(null);
  const gaze = useRef<Gaze>({ lx: 0, ly: 0, near: 0 });
  const moodRef = useRef(mood);
  moodRef.current = mood;
  const [blink, setBlink] = useState(false);
  const [hop, setHop] = useState(0);

  /** Puts the eyes, the lean and the smile where the gaze says (in place: no React render). */
  const apply = useRef(() => {});
  apply.current = () => {
    const m = moodRef.current;
    let { lx, ly } = gaze.current;
    const { near } = gaze.current;
    if (m === "reflechit") {
      lx = 0.7;
      ly = -0.9;
    }
    const wide = m === "ecoute" ? 1.15 : 1 + near * 0.12;
    const rx = (EYE.rx * wide).toFixed(2);
    const ry = (EYE.ry * wide).toFixed(2);
    whiteL.current?.setAttribute("rx", rx);
    whiteL.current?.setAttribute("ry", ry);
    whiteR.current?.setAttribute("rx", rx);
    whiteR.current?.setAttribute("ry", ry);
    const r = (EYE.pupil - near * 0.6).toFixed(2);
    pupilL.current?.setAttribute("r", r);
    pupilR.current?.setAttribute("r", r);
    shineL.current?.setAttribute("cx", (eyeL + 1.6 + lx * 2.6).toFixed(2));
    shineR.current?.setAttribute("cx", (eyeR + 1.6 + lx * 2.6).toFixed(2));
    if (lookRef.current) lookRef.current.style.transform = `translate(${(lx * EYE.lookX).toFixed(2)}px, ${(ly * EYE.lookY).toFixed(2)}px)`;
    const lean = m === "ecoute" ? -4 : lx * 5 * (0.4 + near);
    if (leanRef.current) leanRef.current.style.transform = `rotate(${lean.toFixed(2)}deg)`;
    if (smileRef.current) smileRef.current.style.display = near > 0.55 && m === "repos" ? "" : "none";
  };

  // After every render (a new mood changes the eyes' size, the lean, the gaze): the eyes where they belong.
  useLayoutEffect(() => {
    apply.current();
  });

  // The gaze follows the pointer anywhere on the page; idle, it wanders.
  useEffect(() => {
    if (!live) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (fn: () => void, ms: number) => {
      timers.push(setTimeout(fn, ms));
      if (timers.length > 40) timers.splice(0, 20);
    };
    const track = () => {
      const el = svgRef.current;
      if (!el || !pointer.seen) return;
      const b = el.getBoundingClientRect();
      if (!b.width || b.bottom < -200 || b.top > window.innerHeight + 200) return;
      const cx = b.left + b.width / 2;
      const cy = b.top + b.height * 0.48;
      const dx = pointer.x - cx;
      const dy = pointer.y - cy;
      const dist = Math.hypot(dx, dy);
      const k = Math.min(1, dist / Math.max(260, b.width * 2.5));
      const ang = Math.atan2(dy, dx);
      const amp = 0.35 + 0.65 * k;
      gaze.current = { lx: Math.cos(ang) * amp, ly: Math.sin(ang) * amp, near: Math.max(0, 1 - dist / (b.width * 1.4)) };
      apply.current();
    };
    const unfollow = follow(track);
    track();
    // Idle (no move for 2.5 s): looks somewhere else every 0.9 to 2.9 s, back to the center one time in four.
    const wander = () => {
      later(() => {
        if (performance.now() - pointer.at > 2500) {
          const center = Math.random() < 0.25;
          gaze.current = { lx: center ? 0 : rand(-0.9, 0.9), ly: center ? 0 : rand(-0.6, 0.6), near: 0 };
          apply.current();
        }
        wander();
      }, rand(900, 2900));
    };
    wander();
    // Blinks every 1.8 to 5.6 s, the lid closed 110 ms; a double blink one time in four.
    const blinkLoop = () => {
      later(() => {
        setBlink(true);
        later(() => {
          setBlink(false);
          if (Math.random() < 0.25) later(() => {
            setBlink(true);
            later(() => setBlink(false), 100);
          }, 160);
          blinkLoop();
        }, 110);
      }, rand(1800, 5600));
    };
    blinkLoop();
    return () => {
      unfollow();
      for (const t of timers) clearTimeout(t);
    };
  }, [live]);

  const lid = asleep ? 0.1 : blink && !happy ? 0.08 : mood === "parle" ? 0.88 : 1;
  const floatClass = !animated ? "" : happy ? "pz-joy" : asleep ? "pz-float pz-slow" : "pz-float";
  const bodyClass = !animated ? "" : happy ? "pz-joy-squash" : mood === "parle" ? "pz-talk" : "pz-breathe";
  const height = framed ? size : Math.round(size * 1.1);

  return (
    <svg
      ref={svgRef}
      className={cx("pz", className)}
      data-mood={mood}
      data-still={animated ? undefined : "true"}
      width={size}
      height={height}
      viewBox={characterViewBox(framed)}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      style={style}
      onClick={boop && animated ? () => setHop((n) => n + 1) : undefined}
    >
      {mood === "ecoute" && animated ? (
        <>
          <circle className="pz-ring" cx="50" cy="54" r="42" fill="none" stroke={body} strokeWidth="2" />
          <circle className="pz-ring pz-ring2" cx="50" cy="54" r="42" fill="none" stroke={body} strokeWidth="2" />
        </>
      ) : null}
      <ellipse className={animated ? "pz-shadow" : undefined} cx="50" cy="104" rx="26" ry="3.8" fill="#000000" opacity="0.18" />
      <g className={floatClass}>
        <g ref={leanRef} className="pz-lean">
          <g key={`hop${hop}`} className={hop > 0 ? "pz-hop" : undefined}>
            <g className={hop > 0 ? "pz-hop-squash" : undefined}>
              <g className={bodyClass}>
                <path d={geo.d} fill={body} transform={geo.transform ?? undefined} />
                {happy ? (
                  <path d={happyEyesPath(look.shape)} fill="none" stroke={ink} strokeWidth="3.4" strokeLinecap="round" />
                ) : (
                  <g className="pz-lid" style={{ transform: `scaleY(${lid})` }}>
                    <ellipse ref={whiteL} cx={eyeL} cy={EYE.y} rx={EYE.rx} ry={EYE.ry} fill="#FFFFFF" />
                    <ellipse ref={whiteR} cx={eyeR} cy={EYE.y} rx={EYE.rx} ry={EYE.ry} fill="#FFFFFF" />
                    <g ref={lookRef} className="pz-look">
                      <circle ref={pupilL} cx={eyeL} cy={EYE.y} r={EYE.pupil} fill={EYE.pupilColor} />
                      <circle ref={pupilR} cx={eyeR} cy={EYE.y} r={EYE.pupil} fill={EYE.pupilColor} />
                      <circle ref={shineL} cx={eyeL + 1.6} cy={EYE.highlightY} r={EYE.highlight} fill="#FFFFFF" />
                      <circle ref={shineR} cx={eyeR + 1.6} cy={EYE.highlightY} r={EYE.highlight} fill="#FFFFFF" />
                    </g>
                  </g>
                )}
                {mood === "parle" ? <ellipse className={animated ? "pz-mouth" : undefined} cx="50" cy="69" rx="3.8" ry="3.4" fill={ink} /> : null}
                {happy ? (
                  <path d={SMILE_PATH} fill="none" stroke={ink} strokeWidth="2.8" strokeLinecap="round" />
                ) : mood === "repos" ? (
                  <path ref={smileRef} d={SMILE_PATH} fill="none" stroke={ink} strokeWidth="2.8" strokeLinecap="round" style={{ display: "none" }} />
                ) : null}
              </g>
            </g>
          </g>
        </g>
        {mood === "reflechit" ? (
          <g className={animated ? "pz-orbit" : undefined}>
            <circle cx="50" cy="6" r="3.4" fill={body} />
          </g>
        ) : null}
      </g>
    </svg>
  );
}

/**
 * The agent's face at `size` px. An image sits in a round frame (a ring says what the agent is doing, a badge from
 * 36 px); the drawn character stands free with its ground shadow when `bare` (then it is `size` wide and 1.1 × `size`
 * tall), else it fits a square. `status` drives the character's mood (`mood` overrides it). `label` names it for
 * assistive tech; without it the avatar is decorative (the name is written next to it).
 */
export function AgentAvatarView({
  agent,
  size = 40,
  status,
  mood,
  bare = false,
  tile = false,
  animated = true,
  label,
  className,
}: {
  agent: AvatarAgent;
  size?: number;
  status?: PresenceState;
  mood?: CharacterMood;
  bare?: boolean;
  /** A rounded square instead of a circle (an image avatar only). */
  tile?: boolean;
  animated?: boolean;
  label?: string;
  className?: string;
}) {
  const drawn = !agent.avatar;
  const free = drawn && bare;
  const badge = !drawn && size >= 36 && (status === "computer" || status === "paused");
  return (
    <span
      data-testid="agent-avatar"
      data-avatar={drawn ? "drawn" : "image"}
      data-status={status ?? "idle"}
      data-bare={free ? "true" : undefined}
      data-tile={tile && !drawn ? "true" : undefined}
      className={cx("agent-avatar", className)}
      style={{ width: size, height: free ? Math.round(size * 1.1) : size }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {agent.avatar ? (
        <span className="agent-avatar-face">
          <img src={fileUrl(agent.avatar.fileKey)} alt="" draggable={false} decoding="async" />
        </span>
      ) : (
        <Perso look={lookOf({ ...agent, main: agent.main ?? true })} size={size} framed={!free} mood={mood ?? moodOf(status)} asleep={status === "paused"} animated={animated && size >= 18} />
      )}
      {!drawn && status && status !== "idle" ? <span className="agent-avatar-ring" aria-hidden="true" /> : null}
      {badge ? (
        <span className="agent-avatar-badge" aria-hidden="true">
          {status === "computer" ? <IconComputer width="70%" height="70%" strokeWidth={2.4} /> : <IconPause width="70%" height="70%" strokeWidth={2.4} />}
        </span>
      ) : null}
    </span>
  );
}
